import { h, Fragment } from 'preact'
import { useState } from 'preact/hooks'

import Form from 'components/Form'
import Switch from 'components/Switch'
import OrganizationEditForm from 'components/OrganizationEditForm'

export default function ExternalAppsSettingsPage({
  organization, changes, setChanges
}) {
  return <OrganizationEditForm {...{
    className: 'OrganizationAdminPage-padded',
    organization,
    value: changes,
    onChange: setChanges,
    render: props => {
      return <Fragment>
        <ExternalAppsEnableSection {...{...props, organization}}/>
        <ExternalAppsListSection {...{...props, organization}}/>
        {props.formControls()}
      </Fragment>
    }
  }}/>
}

function ExternalAppsEnableSection({ form }) {
  return <Form.Row>
    <Form.Item>
      <Form.Label>ENABLE EXTERNAL APPS</Form.Label>
      <p>Enable embedded external apps (like playLab) for your hub. Only visible to members.</p>
      {
        form.bindInput({
          valueProp: 'external_apps_enabled',
          validateOnChange: true,
          input: <Switch />
        })
      }
    </Form.Item>
  </Form.Row>
}

function ExternalAppsListSection({ form, organization }) {
  const [newAppName, setNewAppName] = useState('')
  const [newAppUrl, setNewAppUrl] = useState('')
  const externalAppsEnabled = form.getValue('external_apps_enabled') ?? organization.external_apps_enabled

  // Helper function to get current external apps settings
  const getCurrentExternalAppsSettings = () => {
    const currentSettings = form.getValue('external_apps_settings') || organization.external_apps_settings || {}
    return {
      apps: [],
      ...currentSettings
    }
  }

  // Helper function to update external apps settings
  const updateExternalAppsSettings = (newSettings) => {
    const currentFormValue = form.getValue() || {}
    const updatedFormValue = {
      ...currentFormValue,
      external_apps_settings: newSettings
    }

    if (form.onChange) {
      form.onChange(updatedFormValue)
    }
  }

  const currentSettings = getCurrentExternalAppsSettings()
  const apps = currentSettings.apps

  const addApp = () => {
    if (newAppName.trim() && newAppUrl.trim()) {
      // Validate URL format
      try {
        new URL(newAppUrl)
      } catch (e) {
        // eslint-disable-next-line no-alert
        alert('Please enter a valid URL')
        return
      }

      if (!apps.some(app => app.url === newAppUrl.trim())) {
        const updatedApps = [...apps, { name: newAppName.trim(), url: newAppUrl.trim() }]
        const newSettings = { ...currentSettings, apps: updatedApps }
        updateExternalAppsSettings(newSettings)
        setNewAppName('')
        setNewAppUrl('')
      } else {
        // eslint-disable-next-line no-alert
        alert('This URL is already added')
      }
    } else {
      // eslint-disable-next-line no-alert
      alert('Please enter both app name and URL')
    }
  }

  const removeApp = (appUrl) => {
    const updatedApps = apps.filter(app => app.url !== appUrl)
    const newSettings = { ...currentSettings, apps: updatedApps }
    updateExternalAppsSettings(newSettings)
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addApp()
    }
  }

  return <Fragment>
    <Form.Row>
      <Form.Item>
        <Form.Label>EMBEDDED APPS</Form.Label>
        <p>Add embedded apps (iframe) that will appear only to hub members. These could be playLab, learning platforms, tools, or any other embedded content.</p>

        {/* Existing Apps */}
        {apps.length > 0 && (
          <div className="external-apps-list" style={{ marginBottom: '1rem' }}>
            {apps.map((app, index) => (
              <div key={index} className="app-item" style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '0.5rem',
                padding: '0.75rem',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>{app.name}</div>
                  <div style={{ fontSize: '0.9rem', color: '#666', wordBreak: 'break-all' }}>{app.url}</div>
                </div>
                <button
                  type="button"
                  onClick={() => removeApp(app.url)}
                  disabled={!externalAppsEnabled}
                  style={{
                    marginLeft: '0.5rem',
                    padding: '0.25rem 0.5rem',
                    backgroundColor: '#ff4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '3px',
                    cursor: externalAppsEnabled ? 'pointer' : 'not-allowed',
                    opacity: externalAppsEnabled ? 1 : 0.5,
                    whiteSpace: 'nowrap'
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add New App */}
        <div style={{ marginTop: '1rem' }}>
          <Form.Label style={{ marginBottom: '0.5rem', display: 'block' }}>ADD NEW APP</Form.Label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <input
              type="text"
              value={newAppName}
              onChange={(e) => setNewAppName(e.target.value)}
              placeholder="App name (e.g., playLab, Learning Hub)"
              disabled={!externalAppsEnabled}
              style={{
                padding: '0.5rem',
                border: '1px solid #ccc',
                borderRadius: '4px',
                opacity: externalAppsEnabled ? 1 : 0.5
              }}
            />
            <input
              type="url"
              value={newAppUrl}
              onChange={(e) => setNewAppUrl(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Embed URL (e.g., https://www.playlab.ai/community/...)"
              disabled={!externalAppsEnabled}
              style={{
                padding: '0.5rem',
                border: '1px solid #ccc',
                borderRadius: '4px',
                opacity: externalAppsEnabled ? 1 : 0.5
              }}
            />
          </div>
          <button
            type="button"
            onClick={addApp}
            disabled={!externalAppsEnabled || !newAppName.trim() || !newAppUrl.trim()}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: (externalAppsEnabled && newAppName.trim() && newAppUrl.trim()) ? 'pointer' : 'not-allowed',
              opacity: (externalAppsEnabled && newAppName.trim() && newAppUrl.trim()) ? 1 : 0.5,
              width: '100%'
            }}
          >
            Add App
          </button>
        </div>
      </Form.Item>
    </Form.Row>
  </Fragment>
}
